import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { put } from "aws-amplify/api";
import Moment from "moment";
import "containers/common/List.css";
import "containers/common/Table.css";
import { GenericTable } from "components/GenericTable";
import { Link } from "react-router-dom";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";


function HybridProofingCategory({ id, name, description, items, user, isAuthenticated, onRefreshRequested  }) {

  const categoryIdPrefix = `hybrid-proofing-category-${id}`;

  const [stateItems, setStateItems] = useState(items);
  const [sorting, setSorting] = useState([]);

  function getPropertyKeyValue(item) {
    const idColumn = Object.entries(item.action_item).find(([_, value]) => value.IsId === true);
    return idColumn ? idColumn[1].Value : undefined;
  }

  function isSelectedValue(value) {
    if (value === true || value === 1) return true;
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "y" || normalized === "yes";
    }
    return false;
  }

  const buildInitialRowSelection = useCallback((nextItems) => {
    const initialSelection = {};

    (nextItems || []).forEach((item) => {
      const key = getPropertyKeyValue(item);
      if (isSelectedValue(item.selected) && key !== undefined) {
        initialSelection[String(key)] = true;
      }
    });

    return initialSelection;
  }, []);

  const [rowSelection, setRowSelection] = useState(() => buildInitialRowSelection(items));

  useEffect(() => {
    setStateItems(items);
    setRowSelection(buildInitialRowSelection(items));
  }, [items, buildInitialRowSelection]);

  async function handleActionedCheckbox(queue) {
    const queueIndex = stateItems.findIndex((item) => item.queue_id === queue.queue_id);
    if (queueIndex < 0) return;

    const updatedItem = { ...stateItems[queueIndex] };

    updatedItem.actioned_date =
      updatedItem.actioned_date === null ? new Date().toISOString() : null;

    updatedItem.actioned_by = updatedItem.actioned_by === null ? user : null;

    await updateRecord(queueIndex, updatedItem);
  }

  async function handleClosedCheckbox(queue) {
    const queueIndex = stateItems.findIndex((item) => item.queue_id === queue.queue_id);
    if (queueIndex < 0) return;

    const updatedItem = { ...stateItems[queueIndex] };

    updatedItem.closed_date =
      updatedItem.closed_date === null ? new Date().toISOString() : null;

    updatedItem.closed_by = updatedItem.closed_by === null ? user : null;

    await updateRecord(queueIndex, updatedItem);
  }

  async function updateRecord(queueIndex, updatedQueueItem) {
    const errorMessage = "Unable to update status.";
    let response = null;

    try {
      response = await put({
        apiName: "colin",
        path: `/proofing/queues/${updatedQueueItem.queue_id}`,
        options: {
          body: updatedQueueItem,
        },
      }).response;
    } catch {
      alert(errorMessage);
      return;
    }

    let responseBody = null;
    try {
      responseBody = await response.body.json();
    } catch {
      alert(errorMessage);
      return;
    }

    if (responseBody.result === false) {
      alert(errorMessage);
      return;
    }

    setStateItems((prevItems) =>
      prevItems.map((item, index) =>
        index === queueIndex ? updatedQueueItem : item
      )
    );

    if (onRefreshRequested) {
      await onRefreshRequested();
    }
  }


  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const columnHelper = createColumnHelper();

  const createId = useMemo(
    () => createElementIdFactory(categoryIdPrefix),
    [categoryIdPrefix]
  );

  const tableElementIds = useMemo(
    () => ({
      ...createReusableTableElementIds(createId, "main"),
      container: createId("table", "container"),
    }),
    [createId]
  );
  
  const columns = useMemo(() => {
    if (!stateItems || stateItems.length === 0) return [];

    const actionItemKeys = Array.from(
      new Set(
        stateItems.flatMap((row) => Object.keys(row.action_item || {}))
      )
    );

    const actionItemColumns = actionItemKeys.map(key => ({
      header: key,
      accessorFn: (row) => row.action_item[key]?.Value ?? "",
      cell: (info) => {
        const item = info.row.original.action_item[key];
        const {
          Type,
          HyperlinkUrl
        } = item || {};

        if (key === "Property Key" && Type === "Hyperlink" && HyperlinkUrl) {
          let url = HyperlinkUrl;
          if (selectedIdsRef.current.length > 0) {
            const separator = url.includes("?") ? "&" : "?";
            url += `${separator}browselist=${selectedIdsRef.current.join(",")}`;
          }
          // Validation on click
          const handlePropertyKeyClick = (e) => {
            if (selectedIdsRef.current.length > 1000) {
              alert(`Too many queue items selected (${selectedIdsRef.current.length}).\nPlease keep selection to no more than 1000.`);
              e.preventDefault();
            }
          };
          return (
            <Link
              id={createId("property", "key", "link", info.row.id)}
              to={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePropertyKeyClick}
            >
              {item.Value}
            </Link>
          );
        }
        return <>{info.getValue()}</>;
      },
    }));

    return [
      columnHelper.accessor("selected", {
        header: ({ table }) => (
          <input
            id={createId("select", "all", "checkbox")}
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              table.getToggleAllRowsSelectedHandler()(e);
            }}
          />
        ),
        cell: ({ row }) => (
          <input
            id={createId("select", "row", "checkbox", row.id)}
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={(e) => {
              e.stopPropagation();
              row.getToggleSelectedHandler()(e);
            }}
          />
        ),
        meta: { align: 'center' },
      }),
      ...actionItemColumns,
      columnHelper.display({
        id: "actioned_toggle",
        header: "Actioned?", 
        cell: (info) => (
          <input
            id={createId("actioned", "toggle", info.row.id)}
            type="checkbox"
            checked={Boolean(info.row.original.actioned_date)}
            onClick={(e) => e.stopPropagation()}
            onChange={async (e) => {
              e.stopPropagation();
              await handleActionedCheckbox(info.row.original);
            }}
          />
        ),
        meta: { align: 'center' },
      }),
      columnHelper.accessor("actioned_by", {
        header: "Actioned By",
        cell: (info) => info.getValue() || "",
      }),
      columnHelper.accessor("actioned_date", {
        header: "Actioned Date",
        cell: (info) => info.getValue() ? Moment(info.getValue()).format("DD-MM-YYYY") : "",
      }),
      columnHelper.display({
        id: "closed_toggle",
        header: "Closed?", 
        cell: (info) => (
          <input
            id={createId("closed", "toggle", info.row.id)}
            type="checkbox"
            checked={Boolean(info.row.original.closed_date)}
            onClick={(e) => e.stopPropagation()}
            onChange={async (e) => {
              e.stopPropagation();
              await handleClosedCheckbox(info.row.original);
            }}
          />
        ),
        meta: { align: 'center' },
      }),
      columnHelper.accessor("closed_by", {
        header: "Closed By",
        cell: (info) => info.getValue() || "",
      }),
      columnHelper.accessor("closed_date", {
        header: "Closed Date",
        cell: (info) => info.getValue() ? Moment(info.getValue()).format("DD-MM-YYYY") : "",
      })
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createId, stateItems]);


  // --- Render Functions ---

  function render() {
    return (
      <Accordion.Item id={createId("item")} eventKey={String(id)}>
        <Accordion.Header id={createId("accordion", "header")}>
          <span id={createId("title")} className="proofing-accordion-title">
            {name}: Fail results:{" "}
            {stateItems.length > 0 ? (
              <span className="proofing-fail-count">{stateItems.length}</span>
            ) : (
              0
            )}
          </span>
        </Accordion.Header>
        <Accordion.Body id={createId("accordion", "body")} className="px-0">
          <div id={createId("body")} className="px-0">
            <p id={createId("description")} className="px-3">{description}</p>
            {stateItems.length > 0 && (
              <div id={createId("accordion", "table", "wrapper")} className="w-100">
                <GenericTable 
                  data={stateItems} 
                  columns={columns}
                  section="hybrid-proofing-category"
                  elementIds={tableElementIds}
                  pageSize={PAGE_SIZES.adHocProofingPanel}
                  enablePagination={true}
                  state={{ sorting }}
                  onSortingChange={setSorting}
                  enableSortingRemoval={false}
                  enableRowSelection={true}
                  getRowId={(row) => String(getPropertyKeyValue(row))}
                  rowSelection={rowSelection}
                  onRowSelectionChange={setRowSelection}
                />
              </div>
            )}
          </div>
        </Accordion.Body>
      </Accordion.Item>
    );
  }
  if (!isAuthenticated) return null;

  return <div id={createId("container")} className="HybridProofingCategory">{render()}</div>;
}

export default HybridProofingCategory;
